import { SelectionModel } from '@angular/cdk/collections';
import { FlatTreeControl } from '@angular/cdk/tree';
import { Component, EventEmitter, Input, OnInit, OnDestroy, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { DataProvider } from 'projects/packcert-web/src/app/providers/data.provider';
import { Message, SpinnerService } from 'shared-components';
import { UsuariosService } from '../../../services/usuarios.service';
import { Subject } from 'rxjs';
import { SessionContextService } from 'projects/packcert-web/src/app/context/session-context.service';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';

/** Flat to-do item node with expandable and level information */
export class TodoItemFlatNode {
  item: string;
  level: number;
  claimType: string;
  expandable: boolean;
}

export class MenuClaims {
  id: number;
  des: string;
  claimType: string;
  children: MenuClaims[];
}

export class UserClaims {
  claimType: string;
  claimValue: boolean;
}

@Component({
  selector: 'app-usr-new-edit',
  templateUrl: './new-edit.component.html'
})
export class UsrNewEditComponent implements OnInit, OnDestroy {

  private readonly destroy$ = new Subject<void>();
  public showError: boolean;
  public showApiError: boolean; // evitar mostrar la clase de error en inputs si falla el getById
  public mensajes = new Array<Message>();
  public rolesUsuarios;
  public newEditForm: UntypedFormGroup;

  public usuario;
  private userClaims: UserClaims[];

  public showPassword = true;
  public usuarioExistente;

  public disabledIdioma: Subject<boolean> = new Subject();

  menuClaims: MenuClaims[];

  @Input() usuarioId: number;
  @Output() refreshData = new EventEmitter<boolean>();
  @Output() mensaje = new EventEmitter<string>();

  public pestanaActiva = 1;

  // TREE VIEW
  /** Map from flat node to nested node. This helps us finding the nested node to be modified */
  flatNodeMap = new Map<TodoItemFlatNode, MenuClaims>();

  /** Map from nested node to flattened node. This helps us to keep the same object for selection */
  nestedNodeMap = new Map<MenuClaims, TodoItemFlatNode>();

  /** A selected parent node to be inserted */
  selectedParent: TodoItemFlatNode | null = null;

  /** The new item's name */
  newItemName = '';

  treeControl: FlatTreeControl<TodoItemFlatNode>;

  treeFlattener: MatTreeFlattener<MenuClaims, TodoItemFlatNode>;

  dataSource: MatTreeFlatDataSource<MenuClaims, TodoItemFlatNode>;

  /** The selection for checklist */
  checklistSelection = new SelectionModel<TodoItemFlatNode>(true /* multiple */);

  getLevel = (node: TodoItemFlatNode) => node.level;

  isExpandable = (node: TodoItemFlatNode) => node.expandable;

  getChildren = (node: MenuClaims): MenuClaims[] => node.children;

  hasChild = (_: number, _nodeData: TodoItemFlatNode) => _nodeData.expandable;

  hasNoContent = (_: number, _nodeData: TodoItemFlatNode) => _nodeData.item === '';

  /**
   * Transformer to convert nested node to flat node. Record the nodes in maps for later use.
   */
  //  transformer = (node: TodoItemNode, level: number) => {
  transformer = (node: MenuClaims, level: number) => {
    const existingNode = this.nestedNodeMap.get(node);
    const flatNode = existingNode && existingNode.item === node.des
      ? existingNode
      : new TodoItemFlatNode();
    flatNode.item = node.des;
    flatNode.level = node.children.length > 0 ? 0 : 1;
    flatNode.claimType = node.claimType;
    if (node.children) {
      flatNode.expandable = !!node.children.length;
    } else {
      flatNode.expandable = false;
    }

    this.flatNodeMap.set(flatNode, node);
    this.nestedNodeMap.set(node, flatNode);
    return flatNode;
  }

  constructor(
    private _fb: UntypedFormBuilder,
    private readonly _spinnerService: SpinnerService,
    private readonly _sessionContext: SessionContextService,
    private readonly _dataProvider: DataProvider,
    private readonly _usuariosService: UsuariosService) {
    this.treeFlattener = new MatTreeFlattener(this.transformer, this.getLevel,
      this.isExpandable, this.getChildren);
    this.treeControl = new FlatTreeControl<TodoItemFlatNode>(this.getLevel, this.isExpandable);
    this.dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);
  }

  ngOnInit() {

    this.newEditForm = this._fb.group({
      nombre: ['', Validators.required],
      apellido: ['', Validators.required],
      rol: ['', Validators.required],
      idioma: ['', Validators.required],
      habilitado: ['true'],
      dobleFactor: [''],
      bloqueo: [''],
      phoneNumber: ['', Validators.pattern('[0-9]{9}')],
      userName: ['', [Validators.required, Validators.pattern('^[A-z0-9._%+-]+@[A-z0-9.-]+\\.[a-z]{2,4}$')]],
      password: ['', Validators.required]
    });

    this.getRolesUsuarios();

    this.newEditForm.get('userName').valueChanges.pipe(takeUntil(this.destroy$)).subscribe((userName) => {
      if (!this.newEditForm.get('userName').hasError('pattern') && !this.usuarioId &&
        this.role === 'Administrador' && userName !== '' && userName !== null) {
        document.getElementById('userName').blur();
        this._spinnerService.showSpinner();
        const usuario = {
          userNameExacto: userName
        };
        this._usuariosService.getAllUsuarios(usuario)
        .pipe(
          debounceTime(1000),
          distinctUntilChanged()
        )
        .toPromise().finally(() => {
          this._spinnerService.hideSpinner();
        }).then((resp) => {
          if (resp.lista.length > 0) {
            this.usuarioExistente = resp.lista[0];
            this.showPassword = false;
            this.newEditForm.get('password').clearValidators();
            this.newEditForm.get('password').updateValueAndValidity();
            this.fillInfo();
          } else {
            this.newEditForm.get('nombre').enable();
            this.newEditForm.get('apellido').enable();
            this.newEditForm.get('phoneNumber').enable();
            this.newEditForm.get('idioma').enable();

            this.disabledIdioma.next(false);

            document.getElementById('userName').focus();

            this.showPassword = true;
            this.newEditForm.get('password').setValidators([Validators.required]);
            this.newEditForm.get('password').updateValueAndValidity();
          }
        });
      } else {
        this.newEditForm.get('nombre').enable();
        this.newEditForm.get('apellido').enable();
        this.newEditForm.get('phoneNumber').enable();
        this.newEditForm.get('idioma').enable();

        this.disabledIdioma.next(false);

        document.getElementById('userName').focus();

        this.showPassword = true;
        this.newEditForm.get('password').setValidators([Validators.required]);
        this.newEditForm.get('password').updateValueAndValidity();
      }
    });

    if (this.usuarioId) {
      this.newEditForm.get('rol').disable();
      this.newEditForm.get('rol').updateValueAndValidity();
      this.getUsuario();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public get isSmallScreen() {
    return this._sessionContext.isSmallScreen();
  }

  public datosUsuario() {
    this.pestanaActiva = 1;
  }

  public permisosUsuario() {
    this.pestanaActiva = 2;
  }

  public close() {
    this.refreshData.emit(false);
  }

  private getUsuario() {
    this._spinnerService.showSpinner();
    this._usuariosService.getUsuario(this.usuarioId).toPromise().finally(() => {
      this._spinnerService.hideSpinner();
    }).then((resp) => {
      this.usuario = resp;

      this.newEditForm.patchValue({
        // rol: this.usuario.roleName,
        nombre: this.usuario.nombre,
        apellido: this.usuario.apellido,
        idioma: this.idiomas.find(x => x.id === this.usuario.idiomaId),
        habilitado: this.usuario.activo,
        dobleFactor: this.usuario.twoFactorEnabled,
        bloqueo: this.usuario.lockoutEnabled,
        phoneNumber: this.usuario.phoneNumber,
        userName: this.usuario.userName,
        password: ''
      });
      this.newEditForm.get('password').clearValidators();
      this.newEditForm.get('password').updateValueAndValidity();
      // this.newEditForm.get('rol').disable();

      // this._usuariosService.getMenuClaims().subscribe({
      //   next: menuClaims => {
      //     this.dataSource.data = menuClaims;

      //     this._usuariosService.getClaimsOfUser(this.usuario.userName, this.usuario.roleName).subscribe({
      //       next: userClaims => {
      //         this.userClaims = userClaims;

      //         this.dataSource.data.forEach(titles => {
      //           titles.children.forEach(claims => {
      //             if (this.userClaims.find(x => x.claimType == claims.claimType)) {
      //               var flatItem = this.transformer(claims, 0);
      //               this.todoLeafItemSelectionToggle(flatItem);
      //             }
      //           });

      //         });
      //       }
      //     });
      //   }
      // });
    }, (error) => {
      this.showApiError = true;
      if (error.status === 420) {
        this.mensajes.push({ class: 'error', message: 'global.error.noAccess' });
      } else {
        this.mensajes.push({ class: 'error', message: 'global.error.noResponse' });
      }
    });
  }

  public get role() {
    return this._sessionContext.sessionUser.role;
  }

  public getRolesUsuarios() {
    this._spinnerService.showSpinner();
    this._usuariosService.getRolesCreateUsuarios().toPromise().finally(() => {
      this._spinnerService.hideSpinner();
    }).then((resp) => {
      this.rolesUsuarios = resp.map(el => ({ id: el.applicationRoleId, des: el.roleName }));
    });
  }

  public get idiomas() {
    return this._dataProvider.idiomas;
  }

  public save() {
    this.checkForm();
    if (this.newEditForm.valid && !this.usuarioId) {
      this.usuario = {
        paisId: 1,
        denominacionId: 1,
        emailConfirmed: true,
        nombre: this.newEditForm.get('nombre').value,
        apellido: this.newEditForm.get('apellido').value,
        userName: this.newEditForm.get('userName').value,
        password: this.newEditForm.get('password').value,
        phoneNumber: this.newEditForm.get('phoneNumber').value,
        idiomaId: this.newEditForm.get('idioma').value ? this.newEditForm.get('idioma').value.id : '',
        roleName: this.newEditForm.get('rol').value.des,
        activo: this.newEditForm.get('habilitado').value ? this.newEditForm.get('habilitado').value : false,
        useTwoFactor: this.newEditForm.get('dobleFactor').value ? this.newEditForm.get('dobleFactor').value : false,
        useLockout: true
      };
      this._usuariosService.createUsuario(this.usuario).toPromise().finally(() => {
        this._spinnerService.hideSpinner();
      }).then((resp) => {
        this.refreshData.emit(true);
        this.mensaje.emit('global.success.itemCreated');
      },
        (error) => {
          if (error.status === 420) {
            switch (error.error.ApiErrorCode) {
              case 1027:
                this.pushMessage(error.error.Descripcion);
                break;
            }
          }
        });
    } else if (this.newEditForm.valid && this.usuarioId) {
      this.usuario = {
        applicationUserId: this.usuarioId,
        nombre: this.newEditForm.get('nombre').value,
        apellido: this.newEditForm.get('apellido').value,
        userName: this.newEditForm.get('userName').value,
        newPassword: this.newEditForm.get('password').value,
        phoneNumber: this.newEditForm.get('phoneNumber').value,
        idiomaId: this.newEditForm.get('idioma').value ? this.newEditForm.get('idioma').value.id : '',
        activo: this.newEditForm.get('habilitado').value ? this.newEditForm.get('habilitado').value : false,
        useTwoFactor: this.newEditForm.get('dobleFactor').value ? this.newEditForm.get('dobleFactor').value : false,
        useLockout: this.newEditForm.get('bloqueo').value ? this.newEditForm.get('bloqueo').value : false
      };
      this._usuariosService.updateUsuario(this.usuario).toPromise().finally(() => {
        this._spinnerService.hideSpinner();
      }).then(
        (resp) => {
          this._usuariosService.updateUserClaims(this.usuario.userName, this.userClaims).toPromise().finally(() => {
          });
          this.refreshData.emit(true);
          this.mensaje.emit('global.success.itemUpdated');
        },
        (error) => {
          this.showError = true;
          this.mensajes.push({ class: 'error', message: 'global.error.noResponse' });
        }
      );
    }
  }

  private checkForm() {
    this.showError = this.newEditForm.invalid;
    if (this.showError) {
      this.mensajes.push({ class: 'error', message: 'global.error.required' });
    }
  }

  /** Whether all the descendants of the node are selected. */
  descendantsAllSelected(node: TodoItemFlatNode): boolean {
    if (this.userClaims) {
      const descendants = this.treeControl.getDescendants(node);
      const descAllSelected = descendants.length > 0 && descendants.every(child => {
        return this.checklistSelection.isSelected(child);
      });
      return descAllSelected;
    } else {
      return false;
    }
  }

  /** Whether part of the descendants are selected */
  descendantsPartiallySelected(node: TodoItemFlatNode): boolean {
    if (this.userClaims) {
      const descendants = this.treeControl.getDescendants(node);
      const result = descendants.some(child => this.checklistSelection.isSelected(child));
      return result && !this.descendantsAllSelected(node);
    } else {
      return false;
    }
  }

  /** Toggle the to-do item selection. Select/deselect all the descendants node */
  itemSelectionToggle(node: TodoItemFlatNode): void {
    this.checklistSelection.toggle(node);

    if (this.usuarioId) {
      if (this.userClaims.find(x => x.claimType == node.claimType)) {
        this.userClaims.find(x => x.claimType == node.claimType).claimValue = this.checklistSelection.isSelected(node);
      } else {
        this.userClaims.push({ claimType: node.claimType, claimValue: this.checklistSelection.isSelected(node) });
      }
    }


    const descendants = this.treeControl.getDescendants(node);
    this.checklistSelection.isSelected(node)
      ? this.checklistSelection.select(...descendants)
      : this.checklistSelection.deselect(...descendants);

    // Force update for the parent
    descendants.forEach(child => {
      this.checklistSelection.isSelected(child);

      if (this.usuarioId) {
        if (this.userClaims.find(x => x.claimType == child.claimType)) {
          this.userClaims.find(x => x.claimType == child.claimType).claimValue = this.checklistSelection.isSelected(child);
        } else {
          this.userClaims.push({ claimType: child.claimType, claimValue: this.checklistSelection.isSelected(child) });
        }
      }
    });
    this.checkAllParentsSelection(node);
  }

  /** Toggle a leaf to-do item selection. Check all the parents to see if they changed */
  todoLeafItemSelectionToggle(node: TodoItemFlatNode): void {
    this.checklistSelection.toggle(node);

    if (this.userClaims.find(x => x.claimType == node.claimType)) {
      this.userClaims.find(x => x.claimType == node.claimType).claimValue = this.checklistSelection.isSelected(node);
    } else {
      this.userClaims.push({ claimType: node.claimType, claimValue: this.checklistSelection.isSelected(node) });
    }

    this.checkAllParentsSelection(node);
  }

  /* Checks all the parents when a leaf node is selected/unselected */
  checkAllParentsSelection(node: TodoItemFlatNode): void {
    let parent: TodoItemFlatNode | null = this.getParentNode(node);
    while (parent) {
      this.checkRootNodeSelection(parent);
      if (this.userClaims.find(x => x.claimType == parent.claimType)) {
        this.userClaims.find(x => x.claimType == parent.claimType).claimValue = this.checklistSelection.isSelected(parent);
      } else {
        this.userClaims.push({ claimType: parent.claimType, claimValue: this.checklistSelection.isSelected(parent) });
      }
      parent = this.getParentNode(parent);
    }
  }

  /** Check root node checked state and change it accordingly */
  checkRootNodeSelection(node: TodoItemFlatNode): void {
    const nodeSelected = this.checklistSelection.isSelected(node);
    const descendants = this.treeControl.getDescendants(node);
    const descAllSelected = descendants.length > 0 && descendants.every(child => {
      return this.checklistSelection.isSelected(child);
    });
    const descAllUnSelected = descendants.length > 0 && descendants.every(child => {
      return !this.checklistSelection.isSelected(child);
    });

    if (nodeSelected && descAllUnSelected) {
      this.checklistSelection.deselect(node);
    } else if (!nodeSelected && !descAllUnSelected) {
      this.checklistSelection.select(node);
    }
  }

  /* Get the parent node of a node */
  getParentNode(node: TodoItemFlatNode): TodoItemFlatNode | null {
    const currentLevel = this.getLevel(node);

    if (currentLevel < 1) {
      return null;
    }

    const startIndex = this.treeControl.dataNodes.indexOf(node) - 1;

    for (let i = startIndex; i >= 0; i--) {
      const currentNode = this.treeControl.dataNodes[i];

      if (this.getLevel(currentNode) < currentLevel) {
        return currentNode;
      }
    }
    return null;
  }

  public refresh(value) {
    if (value) {
      this.refreshData.emit(true);
    }
  }

  public pushMessage(mensaje) {
    this.showError = true;
    this.mensajes.push({ class: 'error', message: mensaje });
  }

  public fillInfo() {
    this.newEditForm.patchValue({
      nombre: this.usuarioExistente.nombre,
      apellido: this.usuarioExistente.apellido,
      phoneNumber: this.usuarioExistente.phoneNumber,
      idioma: this.idiomas.find(x => x.id === this.usuarioExistente.idiomaId),
      dobleFactor: this.usuarioExistente.twoFactorEnabled,
      habilitado: this.usuarioExistente.activo
    });

    this.newEditForm.get('nombre').disable();
    this.newEditForm.get('apellido').disable();
    this.newEditForm.get('phoneNumber').disable();
    this.newEditForm.get('idioma').disable();
    this.newEditForm.get('dobleFactor').disable();
    this.newEditForm.get('habilitado').disable();

    this.disabledIdioma.next(true);

    this.showPassword = false;
    this.newEditForm.get('password').clearValidators();
    this.newEditForm.get('password').updateValueAndValidity();
  }
}
